import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Trash2, CheckCircle2, Archive, Mic } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
    new: "bg-gold text-white",
    contacted: "bg-green-100 text-green-800",
    archived: "bg-lilac-100 text-lilac-700",
};

const fmt = (iso) => {
    try {
        return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
    } catch {
        return iso;
    }
};

const Detail = ({ label, value }) =>
    value ? (
        <div>
            <div className="text-[10px] uppercase tracking-[0.16em] text-lilac-700 font-bold">{label}</div>
            <div className="text-sm text-lilac-900 mt-0.5">{value}</div>
        </div>
    ) : null;

export const BookingsAdmin = ({ headers, onUnauth }) => {
    const [bookings, setBookings] = useState(null);

    const load = () => {
        axios
            .get(`${API}/admin/bookings`, { headers })
            .then((r) => setBookings(r.data))
            .catch((err) => {
                if (err?.response?.status === 401) { toast.error("Session expired."); onUnauth(); }
                else toast.error("Couldn't load bookings.");
            });
    };

    useEffect(load, []);

    const setStatus = async (id, status) => {
        try {
            await axios.patch(`${API}/admin/bookings/${id}`, { status }, { headers });
            setBookings((list) => list.map((b) => (b.id === id ? { ...b, status } : b)));
            toast.success(status === "contacted" ? "Marked as contacted." : "Archived.");
        } catch {
            toast.error("Update failed.");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this booking request permanently?")) return;
        try {
            await axios.delete(`${API}/admin/bookings/${id}`, { headers });
            setBookings((list) => list.filter((b) => b.id !== id));
            toast.success("Deleted.");
        } catch {
            toast.error("Delete failed.");
        }
    };

    if (!bookings) return <p className="text-lilac-700 text-sm">Loading bookings…</p>;

    if (bookings.length === 0) {
        return (
            <div data-testid="bookings-empty" className="rounded-3xl bg-white border border-border p-12 text-center">
                <span className="mx-auto grid place-items-center w-12 h-12 rounded-full bg-lilac-100 text-lilac-900">
                    <Mic className="w-5 h-5" />
                </span>
                <h3 className="mt-4 font-display text-2xl text-lilac-900">No booking requests yet.</h3>
                <p className="mt-2 text-sm text-lilac-700">
                    When someone submits the Speaker Booking form on your Press Kit, it will appear here
                    (and you'll get an email too).
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-4" data-testid="bookings-list">
            {bookings.map((b) => (
                <div key={b.id} className="rounded-3xl bg-white border border-border p-6" data-testid={`booking-card-${b.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                            <div className="flex items-center gap-2">
                                <span className="font-display text-xl text-lilac-900">{b.name}</span>
                                <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${STATUS_STYLES[b.status] || STATUS_STYLES.new}`}>
                                    {b.status}
                                </span>
                            </div>
                            <div className="text-xs text-lilac-700 mt-1">{b.email} · received {fmt(b.created_at)}</div>
                        </div>
                        <div className="flex items-center gap-2">
                            <a
                                href={`mailto:${b.email}?subject=Re%3A%20Speaker%20booking%20—%20Becoming%20H.E.R.`}
                                data-testid={`booking-reply-${b.id}`}
                                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-lilac-900 text-white hover:bg-lilac-700"
                            >
                                <Mail className="w-3 h-3" /> Reply
                            </a>
                            {b.status !== "contacted" && (
                                <button
                                    onClick={() => setStatus(b.id, "contacted")}
                                    data-testid={`booking-contacted-${b.id}`}
                                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-border text-lilac-900 hover:bg-lilac-50"
                                >
                                    <CheckCircle2 className="w-3 h-3" /> Contacted
                                </button>
                            )}
                            {b.status !== "archived" && (
                                <button
                                    onClick={() => setStatus(b.id, "archived")}
                                    data-testid={`booking-archive-${b.id}`}
                                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-border text-lilac-700 hover:bg-lilac-50"
                                >
                                    <Archive className="w-3 h-3" /> Archive
                                </button>
                            )}
                            <button
                                onClick={() => remove(b.id)}
                                data-testid={`booking-delete-${b.id}`}
                                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-red-200 text-red-600 hover:bg-red-50"
                            >
                                <Trash2 className="w-3 h-3" />
                            </button>
                        </div>
                    </div>

                    <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                        <Detail label="Organization" value={b.organization} />
                        <Detail label="Requested talk" value={b.topic} />
                        <Detail label="Event date" value={b.event_date} />
                        <Detail label="Event type" value={b.event_type} />
                        <Detail label="Audience" value={b.audience_size} />
                        <Detail label="Budget" value={b.budget_range} />
                        <Detail label="Location" value={b.location_type} />
                    </div>

                    {b.message && (
                        <p className="mt-4 text-sm text-lilac-700 leading-relaxed bg-lilac-50 rounded-2xl px-4 py-3 whitespace-pre-wrap">
                            {b.message}
                        </p>
                    )}
                </div>
            ))}
        </div>
    );
};
